import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import app from "../firebaseConfig";

const db = getFirestore(app);

const RankingsPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [rankingCriteria, setRankingCriteria] = useState("averageROI");
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [filters, setFilters] = useState({ carType: "", location: "" });
  const [sortedVehicles, setSortedVehicles] = useState([]);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const carsSnapshot = await getDocs(collection(db, "cars"));
        const carList = [];
        carsSnapshot.forEach((docSnap) => {
          carList.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });

        const payoutsSnapshot = await getDocs(collection(db, "payouts"));
        const revenueMap = {};

        payoutsSnapshot.forEach((payoutDoc) => {
          const data = payoutDoc.data();
          if (!data.carID || !data.amount) return;
          if (!revenueMap[data.carID]) revenueMap[data.carID] = 0;
          revenueMap[data.carID] += data.amount;
        });

        const updatedCarList = carList.map((car) => {
          const totalRevenue = revenueMap[car.id] || 0;
          const totalValue = car.totalValue || 0;
          const averageROI = totalValue > 0 ? (totalRevenue / totalValue) * 100 : 0;

          return {
            ...car,
            totalRevenue,
            averageROI,
          };
        });

        setVehicles(updatedCarList);
      } catch (error) {
        console.error("Error fetching rankings:", error);
      }
    };

    fetchRankings();
  }, []);

  useEffect(() => {
    const applyFiltersAndSort = () => {
      const filteredVehicles = vehicles.filter((vehicle) => {
        return (
          (!filters.carType || vehicle.carType === filters.carType) &&
          (!filters.location || vehicle.location === filters.location)
        );
      });

      const sorted = [...filteredVehicles].sort((a, b) => {
        const valueA = a[rankingCriteria] || 0;
        const valueB = b[rankingCriteria] || 0;

        if (sortOrder === "asc") {
          return valueA - valueB;
        }
        return valueB - valueA;
      });

      setSortedVehicles(sorted);
    };

    if (vehicles.length > 0) {
      applyFiltersAndSort();
    }
  }, [vehicles, rankingCriteria, sortOrder, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "auto", fontFamily: "'Roboto', sans-serif", color: "black" }}>
      <h1 style={{ textAlign: "center", marginBottom: "30px" }}>Vehicle Rankings</h1>

      {/* Filters */}
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div>
          <label htmlFor="rankingCriteria" style={{ marginRight: "10px", fontWeight: "bold" }}>Rank By:</label>
          <select
            id="rankingCriteria"
            value={rankingCriteria}
            onChange={(e) => setRankingCriteria(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: "5px", border: "1px solid #ccc" }}
          >
            <option value="averageROI">ROI</option>
            <option value="totalRevenue">Total Revenue</option>
            <option value="totalValue">Investment Value</option>
          </select>
        </div>

        <div>
          <label htmlFor="sortOrder" style={{ marginRight: "10px", fontWeight: "bold" }}>Sort Order:</label>
          <select
            id="sortOrder"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: "5px", border: "1px solid #ccc" }}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <div>
          <label htmlFor="carType" style={{ marginRight: "10px", fontWeight: "bold" }}>Car Type:</label>
          <select
            id="carType"
            name="carType"
            value={filters.carType}
            onChange={handleFilterChange}
            style={{ padding: "5px 10px", borderRadius: "5px", border: "1px solid #ccc" }}
          >
            <option value="">All</option>
            <option value="Rent">Rent</option>
            <option value="Flip">Flip</option>
            <option value="Both">Both</option>
          </select>
        </div>

        <div>
          <label htmlFor="location" style={{ marginRight: "10px", fontWeight: "bold" }}>Location:</label>
          <input
            id="location"
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            placeholder="Enter location"
            style={{ padding: "5px 10px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
        </div>
      </div>

      {/* Rankings Table */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "35px" }}>
        <thead>
          <tr style={{ backgroundColor: "#1e1e1e", textAlign: "left", color: "white" }}>
            <th style={{ padding: "10px" }}>Rank</th>
            <th style={{ padding: "10px" }}>Image</th>
            <th style={{ padding: "10px" }}>Vehicle Name</th>
            <th style={{ padding: "10px" }}>ROI</th>
            <th style={{ padding: "10px" }}>Total Revenue</th>
            <th style={{ padding: "10px" }}>Investment Value</th>
            <th style={{ padding: "10px" }}>Number of Investors</th>
          </tr>
        </thead>
        <tbody>
          {sortedVehicles.map((vehicle, index) => (
            <tr key={vehicle.id} style={{ borderBottom: "1px solid #ddd" }}>
              <td style={{ padding: "10px" }}>{index + 1}</td>
              <td style={{ padding: "10px" }}>
                <img
                  src={vehicle.pictures?.[0] || "https://via.placeholder.com/150"}
                  alt={vehicle.carName || "Vehicle"}
                  style={{ width: "150px", borderRadius: "5px" }}
                />
              </td>
              <td style={{ padding: "10px" }}>
                <Link
                  to={`/cars/${vehicle.id}`}
                  style={{
                    textDecoration: "none",
                    color: "#007bff",
                    fontWeight: "bold",
                  }}
                >
                  {vehicle.carName}
                </Link>
              </td>
              <td style={{ padding: "10px" }}>
                {vehicle.averageROI ? `${vehicle.averageROI.toFixed(2)}%` : "0%"}
              </td>
              <td style={{ padding: "10px" }}>
                ${vehicle.totalRevenue ? vehicle.totalRevenue.toLocaleString() : 0}
              </td>
              <td style={{ padding: "10px" }}>
                ${vehicle.totalValue ? vehicle.totalValue.toLocaleString() : 0}
              </td>
              <td style={{ padding: "10px" }}>
                {Array.isArray(vehicle.investors)
                  ? vehicle.investors.length
                  : 0}
              </td>
            </tr>
          ))}
          {sortedVehicles.length === 0 && (
            <tr>
              <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                No vehicles found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RankingsPage;
